import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, ArrowRight, ArrowLeft, CheckCircle, Warning,
  SpinnerGap, Lock, ClipboardText, Buildings, Target, UserCircle
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";

export default function QuestionnairePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [status, setStatus] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [qRes, sRes] = await Promise.all([
          axios.get(`${API}/api/questionnaire`, { withCredentials: true }),
          axios.get(`${API}/api/questionnaire/status`, { withCredentials: true }),
        ]);
        setQuestionnaire(qRes.data);
        setStatus(sRes.data);
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const updateAnswer = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  const toggleMultiSelect = (qId, value) => {
    setAnswers(prev => {
      const current = prev[qId] || [];
      const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      return { ...prev, [qId]: next };
    });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/questionnaire/submit`, { answers }, { withCredentials: true });
      setResult(data);
    } catch (err) {
      setResult({ status: "error", message: err.response?.data?.detail || "Erreur" });
    }
    setSubmitting(false);
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><SpinnerGap size={32} className="animate-spin text-blue-500" /></div>;

  // Already completed
  if (status?.completed && !result) {
    const statusColors = { approved: "text-teal-600 bg-teal-50", review_required: "text-amber-600 bg-amber-50", blocked: "text-red-600 bg-red-50" };
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center" data-testid="questionnaire-completed">
        <CheckCircle size={64} weight="fill" className="text-teal-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold mb-2">Questionnaire complete</h2>
        <p className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${statusColors[status.status] || ""}`}>
          {status.status === "approved" ? "Profil approuve" : status.status === "review_required" ? "En cours de validation" : "En verification"}
        </p>
        <div className="mt-6">
          <button onClick={() => navigate("/dashboard")} className="px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-500">
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  // Result after submit
  if (result) {
    const isOk = result.status === "approved";
    const isReview = result.status === "review_required";
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center" data-testid="questionnaire-result">
        {isOk ? <CheckCircle size={64} weight="fill" className="text-teal-500 mx-auto mb-4" /> :
         isReview ? <Warning size={64} weight="fill" className="text-amber-500 mx-auto mb-4" /> :
         <Lock size={64} weight="fill" className="text-red-500 mx-auto mb-4" />}
        <h2 className="text-xl font-bold mb-2">{result.message}</h2>
        {isOk && (
          <button onClick={() => navigate("/dashboard")} className="mt-6 px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-500" data-testid="go-dashboard-btn">
            Commencer a utiliser KNORIA
          </button>
        )}
      </div>
    );
  }

  if (!questionnaire) return null;

  const section = questionnaire.sections[currentSection];
  const sectionIcons = [UserCircle, Target, ShieldCheck];
  const SectionIcon = sectionIcons[currentSection] || ClipboardText;
  const totalSections = questionnaire.sections.length;

  const allRequiredAnswered = section.questions.every(q => {
    if (!q.required) return true;
    const a = answers[q.id];
    if (q.type === "multiselect") return a && a.length > 0;
    if (q.type === "checkbox") return a === true;
    return a && a !== "";
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#08123A] to-[#0a1628] flex items-center justify-center p-4" data-testid="questionnaire-page">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white tracking-tight">{questionnaire.title}</h1>
          <p className="text-sm text-slate-400 mt-2">{questionnaire.description}</p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-6">
          {questionnaire.sections.map((s, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1.5 rounded-full transition-all ${i <= currentSection ? "bg-blue-500" : "bg-white/10"}`} />
              <p className={`text-[10px] mt-1 ${i <= currentSection ? "text-blue-400" : "text-white/30"}`}>{s.title}</p>
            </div>
          ))}
        </div>

        {/* Section Card */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <SectionIcon size={20} className="text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">{section.title}</h2>
              {section.description && <p className="text-xs text-slate-400">{section.description}</p>}
            </div>
          </div>

          <div className="space-y-5">
            {section.questions.map(q => (
              <div key={q.id} data-testid={`question-${q.id}`}>
                <label className="text-sm font-medium text-slate-200 block mb-2">
                  {q.label} {q.required && <span className="text-red-400">*</span>}
                </label>

                {q.type === "text" && (
                  <input
                    value={answers[q.id] || ""}
                    onChange={e => updateAnswer(q.id, e.target.value)}
                    placeholder={q.placeholder || ""}
                    className="w-full h-11 px-4 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:ring-2 focus:ring-blue-500 outline-none"
                    data-testid={`input-${q.id}`}
                  />
                )}

                {q.type === "select" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {q.options.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => updateAnswer(q.id, opt.value)}
                        className={`p-3 rounded-lg border text-left text-sm transition-all ${
                          answers[q.id] === opt.value
                            ? "bg-blue-500/20 border-blue-500 text-white"
                            : "bg-white/5 border-white/10 text-slate-300 hover:border-white/20"
                        }`}
                        data-testid={`option-${q.id}-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}

                {q.type === "multiselect" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {q.options.map(opt => {
                      const selected = (answers[q.id] || []).includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggleMultiSelect(q.id, opt.value)}
                          className={`p-3 rounded-lg border text-left text-sm transition-all flex items-center gap-2 ${
                            selected
                              ? "bg-blue-500/20 border-blue-500 text-white"
                              : "bg-white/5 border-white/10 text-slate-300 hover:border-white/20"
                          }`}
                          data-testid={`option-${q.id}-${opt.value}`}
                        >
                          <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${selected ? "bg-blue-500 border-blue-500" : "border-white/20"}`}>
                            {selected && <CheckCircle size={10} weight="fill" className="text-white" />}
                          </div>
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {q.type === "checkbox" && (
                  <button
                    type="button"
                    onClick={() => updateAnswer(q.id, !answers[q.id])}
                    className={`p-4 rounded-lg border text-left text-sm transition-all flex items-start gap-3 w-full ${
                      answers[q.id]
                        ? "bg-teal-500/10 border-teal-500 text-white"
                        : "bg-white/5 border-white/10 text-slate-300 hover:border-white/20"
                    }`}
                    data-testid={`checkbox-${q.id}`}
                  >
                    <div className={`w-5 h-5 mt-0.5 rounded border flex-shrink-0 flex items-center justify-center ${answers[q.id] ? "bg-teal-500 border-teal-500" : "border-white/20"}`}>
                      {answers[q.id] && <CheckCircle size={12} weight="fill" className="text-white" />}
                    </div>
                    <span className="leading-relaxed">{q.label}</span>
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
            <button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="prev-section-btn"
            >
              <ArrowLeft size={16} /> Precedent
            </button>
            {currentSection < totalSections - 1 ? (
              <button
                onClick={() => setCurrentSection(currentSection + 1)}
                disabled={!allRequiredAnswered}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="next-section-btn"
              >
                Suivant <ArrowRight size={16} />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={!allRequiredAnswered || submitting}
                className="flex items-center gap-2 px-6 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="submit-questionnaire-btn"
              >
                {submitting ? <SpinnerGap size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                Soumettre
              </button>
            )}
          </div>
        </div>

        {/* Legal notice */}
        <p className="text-[10px] text-slate-500 text-center mt-4 max-w-lg mx-auto leading-relaxed">
          {questionnaire.legal_notice}
        </p>
      </div>
    </div>
  );
}
